// @ts-nocheck

import _, {
  size as _size,
  map as _map,
  find as _find,
  countBy as _countBy,
  reduce as _reduce,
  debounce as _debounce,
  filter as _filter,
} from 'lodash';

import { Target } from '../client';
import config from '../config/app.config';
import Formatter from '../i18n/formatter';
import { __, getCurrency, getDecimalSeparator, getLang } from '../i18n/l10n';
import { selectBoomGlobalBoomId } from '../modules/boom/selectors/global/globalPrimary.selector';
import { selectIsBoomPlayable } from '../modules/boom/selectors/runtime.selector';
import { selectBoomCurrentStepStepId } from '../modules/boom/selectors/steps.selector';
import { type BasketItems } from '../states/basket';
import { type MatchState } from '../states/entities/matches';
import { type BasketMode, BasketModes, BasketItemStatuses, BetslipErrorCodes } from '../types/basket';
import NumberUtils from '../utils/number';
import storage from '../utils/storage';

import { getVersion } from './common';
import {
  createSingleBetslipSubmitPayload,
  createMultipleBetslipSubmitPayload,
  createSystemBetslipSubmitPayload,
} from './createBetslipSubmitPayloadUtils';

const {
  basket: { version: configVersion, defaultStake },
} = config;

const STORAGE_KEY = 'basket';
const STORAGE_VERSION = configVersion;

const getMode = (
  mode: BasketMode,
  items: BasketItems,
  {
    matches,
  }: {
    matches: {
      [key: number]: MatchState;
    };
  },
) => {
  const myMatchItems = _filter(items, (m) => m.betbuilder);
  const sizeItems = _size(items);

  if (myMatchItems.length) {
    if (mode === BasketModes.SYSTEM && sizeItems > 2) {
      return BasketModes.SYSTEM;
    }

    return BasketModes.COMBINED;
  }

  if (mode === BasketModes.SYSTEM) {
    if (sizeItems > 2) {
      return mode;
    }
  }

  const itemsMatches = _map(items, ({ matchId }) => matches[matchId]);
  // On filtrer les outrights car il peut y avoir plusieurs bet sur un meme match (outright) #129595
  const filteredMatches = _filter(itemsMatches, (m) => !m?.isOutright);

  // Find if we have more than one bet for the same match => SIMPLE
  const foundMoreThanOne = _find(_countBy(filteredMatches, 'matchId'), (x) => x > 1);
  if (foundMoreThanOne) {
    return BasketModes.SIMPLE;
  }

  return sizeItems > 1 ? BasketModes.COMBINED : BasketModes.SIMPLE;
};

const checkIfItemCanBeAdded = ({ match, bet }) => !!match && !!bet;

const computePotentialGain = (value, stake, isFreebet): number => {
  if (!isNaN(value) && !isNaN(stake)) {
    let finalValue = value;
    if (isFreebet === true) {
      // remove stake
      finalValue = value - 1;
    }
    let gain = finalValue * stake;
    // https://redmine.winamax.fr/issues/117301
    if (value < 1.1) {
      gain = Math.floor(gain * 100) / 100;
      gain = NumberUtils.roundDownwardCents(gain, 2);
    }
    return Math.max(0, gain);
  }
  return 0;
};

const getBoosterPotentialGain = (options) => {
  const { booster, totalOdd, stake } = options;
  if (booster !== undefined && totalOdd !== undefined && stake !== undefined) {
    const { current } = booster;
    if (current !== undefined) {
      const percent = (current * 100 - 100) / 100;
      return Math.max(0, totalOdd - 1) * Math.max(0, stake) * percent;
    }
  }
  return 0;
};

const formatStake = (stake) => {
  const decimalSeparator = getDecimalSeparator();
  let result = stake;

  // Convert stake into string if is not
  if (typeof stake !== 'string') {
    result = stake.toString();
  }

  // Remove all leading zero
  result = result.replace(decimalSeparator, '.').replace(/^0+/, '0');

  // Find the separator position
  const indexOfSeparator = result.replace('.', decimalSeparator).indexOf(decimalSeparator);

  // Rajouter un zero si le decimal separator est le premier caractere
  if (indexOfSeparator === 0) {
    result = `0${result}`;
  }

  return result;
};

const stakeRegExp = /(?:^[0-9]{0,12}(?:[.,][0-9]{0,2})?)$|^$/;
const isValidStake = (stake) => stakeRegExp.test(stake);

const hasErrorRelativeToStake = (ecode) => {
  return (
    [BetslipErrorCodes.AMOUNT, BetslipErrorCodes.AMOUNT_UNDER_LIMIT, BetslipErrorCodes.AMOUNT_OVER_LIMIT].indexOf(
      ecode,
    ) !== -1
  );
};

const getErrorCodeMessage = ({ ecode, einfo, estr }) => {
  let message = '';
  // eslint-disable-next-line sonarjs/max-switch-cases
  switch (ecode) {
    case BetslipErrorCodes.ACCESS_DENIED: {
      message = __('You must be logged in to validate your bets');
      break;
    }
    case BetslipErrorCodes.BAD_REQUEST: {
      message = __('Internal error');
      break;
    }
    case BetslipErrorCodes.MAINTENANCE: {
      message = __('Maintenance in progress');
      break;
    }
    case BetslipErrorCodes.SAME_BET: {
      message = __('You have already placed the same bet recently');
      break;
    }
    case BetslipErrorCodes.LOCK_FAILED: {
      message = __('Error, please try again');
      break;
    }
    case BetslipErrorCodes.NO_POSSIBLE_WININGS: {
      message = __('Winnings inferior or equal to the wager');
      break;
    }
    case BetslipErrorCodes.INCORRECT_ODD: {
      message = __('Odds equal or inferior to 1');
      break;
    }
    case BetslipErrorCodes.DB_ERROR: {
      message = __('Internal error');
      break;
    }
    case BetslipErrorCodes.NOT_ENOUGH_MONEY: {
      if (einfo !== undefined && einfo.max !== undefined) {
        const amount = parseFloat(einfo.max / 100);
        message = __('You only have %1$s', Formatter.formatNumber(amount, getCurrency()));
      }
      break;
    }
    case BetslipErrorCodes.ODDS_UNDER_LIMIT: {
      if (einfo !== undefined && einfo.min !== undefined) {
        if (!isNaN(einfo.min)) {
          const odd = parseFloat(einfo.min / 1000);
          message = __('The minimum odds are %1$s', Formatter.formatNumber(odd));
        }
      }
      break;
    }
    case BetslipErrorCodes.AMOUNT: {
      message = __('Incorrect amount');
      break;
    }
    case BetslipErrorCodes.AMOUNT_UNDER_LIMIT: {
      if (einfo !== undefined && einfo.min !== undefined) {
        if (!isNaN(einfo.min)) {
          const amount = parseFloat(einfo.min / 100);
          message = __('The minimum wager is %1$s', Formatter.formatNumber(amount, getCurrency()));
        }
      }
      break;
    }
    case BetslipErrorCodes.AMOUNT_OVER_LIMIT: {
      if (einfo !== undefined && einfo.max !== undefined) {
        if (!isNaN(einfo.max)) {
          const amount = parseFloat(einfo.max / 100);
          if (amount < 0.1) {
            // __('The maximum stake for this bet has already been reached, or the stake is lower than the minimum of €0.10')
            message = __(
              'The maximum stake for this bet has already been reached, or the stake is lower than the minimum of €0.10',
            );
          } else {
            message = __('The maximum wager is %1$s', Formatter.formatNumber(amount, getCurrency()));
          }
        }
      }
      break;
    }
    case BetslipErrorCodes.NOT_ENOUGH_FREEBET: {
      if (einfo !== undefined && einfo.max !== undefined) {
        if (!isNaN(einfo.max)) {
          const amount = parseFloat(einfo.max / 100);
          message = __('You only have %1$s in Freebets available', Formatter.formatNumber(amount, getCurrency()));
        }
      }
      break;
    }
    case BetslipErrorCodes.DAILY_MODERATOR_REACHED:
    case BetslipErrorCodes.WEEKLY_MODERATOR_REACHED:
    case BetslipErrorCodes.MONTHLY_MODERATOR_REACHED: {
      if (einfo !== undefined && einfo.max !== undefined) {
        if (!isNaN(einfo.max)) {
          const amount = parseFloat(einfo.max / 100);
          const amountLabel = Formatter.formatNumber(amount, getCurrency());
          if (ecode === BetslipErrorCodes.DAILY_MODERATOR_REACHED) {
            message = __('You will exceed your daily limit. The maximum wager is %1$s', amountLabel);
          } else if (ecode === BetslipErrorCodes.WEEKLY_MODERATOR_REACHED) {
            message = __('You will exceed your weekly limit. The maximum wager is %1$s', amountLabel);
          } else if (ecode === BetslipErrorCodes.MONTHLY_MODERATOR_REACHED) {
            message = __('You will exceed your monthly limit. The maximum wager is %1$s', amountLabel);
          }
        }
      }
      break;
    }
    case BetslipErrorCodes.SYSTEM_TOO_MANY_BANKERS: {
      message = __('Too many bankers');
      break;
    }
    case BetslipErrorCodes.SYSTEM_TOO_MANY_BETSLIP: {
      message = __('Too many bets selected');
      break;
    }
    case BetslipErrorCodes.SYSTEM_TOO_FEW_COMBINATIONS: {
      message = __('Not enough combinations');
      break;
    }
    case BetslipErrorCodes.ODDS_CHANGED:
    case BetslipErrorCodes.BETSLIP_DETAIL_ODDS_CHANGED_DECREASE:
    case BetslipErrorCodes.BETSLIP_DETAIL_ODDS_CHANGED_INCREASE: {
      message = __('The odds have changed');
      break;
    }
    case BetslipErrorCodes.BETSLIP_DETAIL_INCOMPATIBLE_BETS:
    case BetslipErrorCodes.INCOMPATIBLE_BETS: {
      message = __('You cannot combine multiple bets on the same match');
      break;
    }
    case BetslipErrorCodes.BETSLIP_DETAIL_INCOMPATIBLE_OUTRIGHT:
    case BetslipErrorCodes.INCOMPATIBLE_OUTRIGHT: {
      message = __('You cannot combine these bets');
      break;
    }
    case BetslipErrorCodes.BET_SUSPENDED:
    case BetslipErrorCodes.BETSLIP_DETAIL_BET_SUSPENDED: {
      message = __('Bet suspended');
      break;
    }
    case BetslipErrorCodes.BET_STOPPED:
    case BetslipErrorCodes.BETSLIP_DETAIL_BET_STOPPED: {
      message = __('Bet stopped');
      break;
    }
    case BetslipErrorCodes.INVALID_FREEBET_ID: {
      message = __('This free bet cannot be used');
      break;
    }
    case BetslipErrorCodes.INVALID_BOOSTER_ID: {
      message = __('You may not receive a booster on this bet');
      break;
    }
    case BetslipErrorCodes.LIMITED_TO_FREEBETS: {
      message = __('Bet is limited to free bet');
      break;
    }
    case BetslipErrorCodes.TRADER: {
      message = __('Bet pending');
      break;
    }
    case BetslipErrorCodes.BET_MODIFICATION_UNAVAILABLE: {
      message = __('Bet modification unavailable');
      break;
    }
    case BetslipErrorCodes.BET_MODIFICATION_UNAVAILABLE_WITH_FREE_BETS: {
      message = __('Bet modification unavailable with free bets');
      break;
    }
    case BetslipErrorCodes.MYMATCH_PROBLEM:
      message = __('MyMatch unavailable');
      break;
    case BetslipErrorCodes.MYMATCH_UNAVAILABLE_500:
      message = __('Unable to calculate odds');
      break;
    case BetslipErrorCodes.MYMATCH_UNAVAILABLE_1:
      message = __('MyMatch unavailable');
      break;
    case BetslipErrorCodes.MYMATCH_INCOHERENT_SELECTIONS:
      message = __('Contradictory selections');
      break;
    case BetslipErrorCodes.MYMATCH_TOO_MANY_SELECTIONS:
      message = __('Maximum selections reached');
      break;
    case BetslipErrorCodes.MYMATCH_SELECTION_UNAVAILABLE:
      message = __('One or multiple selections are unavailable');
      break;
    case BetslipErrorCodes.TOO_MANY_LINES:
      message = __('Too many selections');
      break;
    case BetslipErrorCodes.MAXIMUM_BET_AMOUNT_REACHED:
      message = __('You have exceeded the maximum stake for this bet');
      break;
    default:
      message = estr || null;
      break;
  }
  return message;
};

const filterValidItems = (items) => {
  return _filter(
    items,
    (item) => item.status !== BasketItemStatuses.VALIDATED && item.status !== BasketItemStatuses.WAITING,
  );
};

const createBetslipRequestPayload = (state) => {
  const {
    mode,
    items,
    stake,
    systems,
    selectedFreebetId,
    selectedBonusId,
    eligibleBooster,
    isCashout,
    cashoutBetslipId,
    eligibleKLCollectibleId,
    isGRActive,
    isFromLineUp,
    monopolyCard,
    isMonopolyActive,
    isBoomActive,
    missionsParams,
  } = state.basket;

  const itemsAsArray = Object.keys(items).map((k) => items[k]);

  const CV = getVersion();

  const locale = getLang();
  const validItems = filterValidItems(items);

  let betslipRequestPayload = undefined;
  const GRRace = state?.gr?.global?.race;
  const isBoomPlayable = state ? selectIsBoomPlayable(state) : false;
  const boomId = isBoomPlayable && state ? selectBoomGlobalBoomId(state) : null;
  const boomStepId = isBoomPlayable && state ? selectBoomCurrentStepStepId(state) : null;
  const isRepeatBet = _.some(items, { isRepeatBet: true });

  let boomBetInfos = undefined;

  if (boomId && boomStepId) {
    boomBetInfos = {
      boomId,
      stepId: boomStepId,
    };
  }

  const isSingleMyMatch = itemsAsArray.length === 1 && itemsAsArray[0]?.betbuilder;

  const overrideDatas = {};
  if (isSingleMyMatch) {
    overrideDatas.stake = stake;
    overrideDatas.selectedFreebetId = selectedFreebetId;
    overrideDatas.selectedBonusId = selectedBonusId;
    overrideDatas.eligibleKLCollectibleId = eligibleKLCollectibleId;
    overrideDatas.isGRActive = isGRActive;
    overrideDatas.isMonopolyActive = isMonopolyActive;
    overrideDatas.monopolyCard = monopolyCard;
    overrideDatas.isBoomActive = isBoomActive;
    overrideDatas.isFromLineUp = isFromLineUp;
    overrideDatas.isRepeatBet = isRepeatBet;
    overrideDatas.missionsParams = missionsParams;
  }

  if (mode === BasketModes.SIMPLE || isSingleMyMatch) {
    betslipRequestPayload = createSingleBetslipSubmitPayload(
      CV,
      locale,
      validItems,
      isCashout,
      cashoutBetslipId,
      GRRace,
      boomBetInfos,
      overrideDatas,
      isRepeatBet,
      missionsParams,
    );
  } else if (mode === BasketModes.COMBINED) {
    betslipRequestPayload = createMultipleBetslipSubmitPayload(
      stake,
      selectedFreebetId,
      validItems,
      isCashout,
      cashoutBetslipId,
      CV,
      locale,
      eligibleBooster,
      selectedBonusId,
      eligibleKLCollectibleId,
      isGRActive,
      GRRace,
      monopolyCard,
      isFromLineUp,
      isRepeatBet,
      isMonopolyActive,
      isBoomActive ? boomBetInfos : undefined,
      missionsParams,
    );
  } else if (mode === BasketModes.SYSTEM) {
    betslipRequestPayload = createSystemBetslipSubmitPayload(
      stake,
      systems,
      validItems,
      CV,
      locale,
      eligibleBooster,
      selectedFreebetId,
      isFromLineUp,
      isRepeatBet,
    );
  } else if (Target.__ENV__ !== 'production') {
    throw new Error('Unhandled basket mode');
  }
  return betslipRequestPayload;
};

/* {p} est le nombre de sélections dans chaque pari combiné */
const generateSystems = ({ items, defaultCheckboxValue }) => {
  const listItemByBanker = _reduce(
    items,
    (finalList, item) => {
      const { banker } = item;
      const key = banker ? 'bankers' : 'nobankers';
      return {
        ...finalList,
        [key]: [...finalList[key], item.value],
      };
    },
    { bankers: [], nobankers: [] },
  );
  const { bankers, nobankers } = listItemByBanker;
  const systems = {};
  if (nobankers.length > 0) {
    for (let i = 1; i <= nobankers.length; i += 1) {
      // let combinations = NumberUtils.getCombinations(nobankers, i);
      // combinations = combinations.map(
      //   combination =>
      //     combination.concat(bankers)
      // );

      const combinations = NumberUtils.getNbCombinations(i, nobankers.length);
      if (combinations <= 1024) {
        // const odds = _reduce(
        //   combinations,
        //   (value, combination) => [
        //     ...value,
        //     _reduce(combination, (a, b) => a * b, 1)
        //   ],
        //   []
        // );

        let title = undefined;
        let help = undefined;
        if (i === nobankers.length) {
          const count = _size(items);
          // eslint-disable-next-line sonarjs/no-duplicate-string
          const betsAccLabel = __('%1$s selection', count, '%1$s selections');
          title = __('Accumulator %1$s', betsAccLabel);
          help = __('The classic accumulator wins if the %1$s are correct', betsAccLabel);
        } else if (bankers.length > 0) {
          const systemLabel = __('System %1$s/%2$s', [i, nobankers.length]);
          const baseLabel = __('%1$s Base', bankers.length, '%1$s Bases');

          title = __('%1$s + %2$s', [baseLabel, systemLabel]);

          const predictionsLabel = __('%1$s selection', i, '%1$s selections');
          const helperCountBaseLabel = __('of the base', bankers.length, 'of %1$s bases');
          const args = [helperCountBaseLabel, predictionsLabel, nobankers.length, combinations];
          help = __('All possible accumulators %1$s with %2$s from %3$s others, or %4$s in total', args);
        } else if (i === 1) {
          title = __('%1$s single bet', nobankers.length, '%1$s single bets');
          help = __('Each simple bet is placed individually');
        } else {
          title = __('System %1$s/%2$s', [i, nobankers.length]);

          const predictionSystemLabel = __('%1$s selection', i, '%1$s selections');
          const systemArgs = [predictionSystemLabel, nobankers.length, combinations];
          help = __('All possible accumulators from the %1$s from amongst %2$s, or %3$s in total', systemArgs);
        }

        systems[i] = {
          k: i,
          title,
          //odds,
          help,
          count: combinations,
          checked: defaultCheckboxValue && i >= nobankers.length - 1,
        };
      }
    }

    return systems;
  }
  // bankers === item.length
  // all items are required
  const combinations = 1; //NumberUtils.getNbCombinations(bankers.length, bankers.length);
  //const combinations = NumberUtils.getCombinations(bankers, bankers.length);
  // const odds = combinations.reduce(
  //   (value, combination) => [
  //     ...value,
  //     _reduce(combination, (a, b) => a * b, 1)
  //   ],
  //   []
  // );
  const count = _size(items);
  const betsAccLabel = __('%1$s selection', count, '%1$s selections');
  const title = __('Accumulator %1$s', betsAccLabel);
  const help = __('The classic accumulator wins if the %1$s are correct', betsAccLabel);

  systems[0] = {
    k: 0,
    title,
    help,
    count: combinations,
    checked: false,
  };

  return systems;
};

const incrementStake = (v, decrement = false) => {
  let increment = undefined;
  let currentValue = Math.max(0, parseFloat(v));
  if (currentValue) {
    if (decrement) {
      if (currentValue === 1) {
        increment = 0.5;
      } else if (currentValue < 1) {
        increment = 0.1;
      } else if (currentValue > 1 && currentValue <= 10) {
        increment = 1;
      } else if (currentValue > 10 && currentValue <= 30) {
        increment = 5;
      } else if (currentValue > 30 && currentValue <= 100) {
        increment = 10;
      } else {
        increment = 50;
      }
    } else if (currentValue < 0.5) {
      increment = 0.1;
    } else if (currentValue === 0.5) {
      increment = 0.5;
    } else if (currentValue < 10) {
      increment = 1;
    } else if (currentValue >= 10 && currentValue < 30) {
      increment = 5;
    } else if (currentValue >= 30 && currentValue < 100) {
      increment = 10;
    } else {
      increment = 50;
    }
  } else {
    currentValue = 0;
    increment = defaultStake;
  }

  let result = Math.round(currentValue * 100 + increment * 100) / 100;
  if (decrement) {
    result = Math.round(currentValue * 100 - increment * 100) / 100;
  }

  if (result <= 0) {
    return 0;
  }
  return result;
};

const loadBasketFromStorage = () => {
  let basket = storage.localStorage.load(STORAGE_KEY, STORAGE_VERSION);

  // if version or licence change, we clear the local storage `basket` key to remove old user data
  if (basket && basket.version !== STORAGE_VERSION) {
    basket = null;
    storage.localStorage.delete(STORAGE_KEY);
  }
  return basket;
};

const saveBasketToStorage = _debounce(
  (data) =>
    storage.localStorage.save(STORAGE_KEY, {
      ...data,
      version: STORAGE_VERSION,
    }),
  300,
);

// eslint-disable-next-line import/no-anonymous-default-export
export default {
  getErrorCodeMessage,
  generateSystems,
  filterValidItems,
  createBetslipRequestPayload,
  checkIfItemCanBeAdded,
  isValidStake,
  formatStake,
  getMode,
  computePotentialGain,
  getBoosterPotentialGain,
  incrementStake,
  loadBasketFromStorage,
  saveBasketToStorage,
  hasErrorRelativeToStake,
};
